import { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import * as api from '../api/loginApi';
import type { ResetPasswordStep } from '../types/login';

interface UseResetPasswordOptions {
    email: string;
    code: string;
}

export const useResetPassword = ({ email, code }: UseResetPasswordOptions) => {
    const intl = useIntl();

    const [step, setStep] = useState<ResetPasswordStep>('loading');
    const [validatedEmail, setValidatedEmail] = useState(email);
    const [pendingPassword, setPendingPassword] = useState('');
    const [resetError, setResetError] = useState<string | undefined>();

    // -----------------------------------------------------------------------
    // Validate the reset token on mount
    // -----------------------------------------------------------------------

    const { data: validationResult, isLoading: isValidating } = useQuery({
        queryKey: ['validateResetToken', email, code],
        queryFn: async () => {
            return api.validateResetToken({ email, code });
        },
        enabled: !!email && !!code,
        retry: false,
    });

    useEffect(() => {
        if (!validationResult) return;

        if (validationResult.success) {
            setStep('form');
            setValidatedEmail(email);
        } else if (validationResult.errorCode === 2) {
            setStep('expired');
        } else {
            setResetError(validationResult.errorMessage);
            setStep('expired');
        }
    }, [validationResult, email]);

    // -----------------------------------------------------------------------
    // Reset password mutation
    // -----------------------------------------------------------------------

    const resetMutation = useMutation({
        mutationFn: async ({
            password,
            ignoreBreachWarning,
        }: {
            password: string;
            ignoreBreachWarning: boolean;
        }) => {
            return api.postResetPassword({
                email: validatedEmail,
                code,
                password,
                confirmPassword: password,
                ignoreBreachWarning,
            });
        },
        onSuccess: (data) => {
            if (data.success) {
                setStep('success');
            } else if (data.passwordBreachWarning) {
                setStep('breached');
            } else if (data.errorCode === 2) {
                setStep('expired');
            } else {
                setResetError(
                    data.errorMessage ??
                        intl.formatMessage({
                            id: 'login.resetPassword.error.resetFailed',
                            defaultMessage: 'Reset failed',
                        }),
                );
            }
        },
    });

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    const submitPassword = (password: string) => {
        setPendingPassword(password);
        setResetError(undefined);
        resetMutation.mutate({ password, ignoreBreachWarning: false });
    };

    const useBreachedPassword = () => {
        resetMutation.mutate({ password: pendingPassword, ignoreBreachWarning: true });
    };

    const chooseAnotherPassword = () => {
        setStep('form');
    };

    return {
        step,
        isValidating,
        isResetting: resetMutation.isPending,
        resetError: resetError ?? resetMutation.error?.message,
        validatedEmail,
        submitPassword,
        useBreachedPassword,
        chooseAnotherPassword,
    };
};
